import React, { useEffect, useState } from "react";
import {
  Table,
  Tabs,
  Typography,
  Tag,
  Spin,
  notification,
  Tooltip,
  Dropdown,
  Modal,
} from "antd";
import type { MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  AgentService,
  AgentUptimeMetrics as AgentUptimeMetricsType,
} from "../../services/AgentService";
import { usePermissions } from "../../PermissionsProvider";
import moment from "moment";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  PoweroffOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
const { TabPane } = Tabs;

const AgentUptimeMetrics: React.FC = () => {
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [metricsByRegion, setMetricsByRegion] = useState<
    Record<string, AgentUptimeMetricsType[]>
  >({});

  useEffect(() => {
    fetchMetrics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, [apiKey]);

  const fetchMetrics = () => {
    AgentService.getAllAgentsUptimeMetricsGlobal(
      setIsLoading,
      apiKey
    ).subscribe(
      (response: {
        metrics_by_region: Record<string, AgentUptimeMetricsType[]>;
      }) => {
        setMetricsByRegion(response.metrics_by_region);
      },
      (error: any) => {
        console.error("Error fetching agent uptime metrics:", error);
        notification.error({
          message: "Error Loading Metrics",
          description: "Failed to load agent uptime metrics. Please try again.",
        });
      }
    );
  };

  const handleDeploy = (agent: AgentUptimeMetricsType) => {
    Modal.confirm({
      title: "Deploy Agent",
      content: `Are you sure you want to deploy agent "${agent.agent_name}"?`,
      okText: "Deploy",
      okType: "primary",
      onOk: () => {
        AgentService.ensureAgentReadyGlobal(
          agent.agent_id,
          setIsLoading,
          apiKey
        ).subscribe(
          () => {
            notification.success({
              message: "Success",
              description: `Agent ${agent.agent_name} deployed successfully`,
            });
            fetchMetrics();
          },
          (error: any) => {
            notification.error({
              message: "Deployment Failed",
              description: error || "Failed to deploy agent",
            });
          }
        );
      },
    });
  };

  const handleScaleDown = (agent: AgentUptimeMetricsType) => {
    Modal.confirm({
      title: "Scale Down Agent",
      content: `Are you sure you want to scale down agent "${agent.agent_name}" to 0 replicas across all regions?`,
      okText: "Scale Down",
      okType: "danger",
      onOk: () => {
        AgentService.scaleDownAgentGlobal(
          agent.agent_id,
          setIsLoading,
          apiKey
        ).subscribe(
          () => {
            notification.success({
              message: "Success",
              description: `Agent ${agent.agent_name} scaled down successfully in all regions`,
            });
            fetchMetrics();
          },
          (error: any) => {
            notification.error({
              message: "Scale Down Failed",
              description: error || "Failed to scale down agent",
            });
          }
        );
      },
    });
  };

  const handleDelete = (agent: AgentUptimeMetricsType) => {
    Modal.confirm({
      title: "Delete Agent",
      content: (
        <div>
          <p>
            Are you sure you want to permanently delete agent "
            {agent.agent_name}"?
          </p>
          <p style={{ color: "red", fontWeight: "bold" }}>
            This action cannot be undone!
          </p>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        AgentService.deleteAgent(
          agent.agent_id,
          setIsLoading,
          apiKey
        ).subscribe(
          () => {
            notification.success({
              message: "Success",
              description: `Agent ${agent.agent_name} deleted successfully`,
            });
            fetchMetrics();
          },
          (error: any) => {
            notification.error({
              message: "Delete Failed",
              description: error || "Failed to delete agent",
            });
          }
        );
      },
    });
  };

  const getActionsMenu = (record: AgentUptimeMetricsType): MenuProps => {
    const items: MenuProps["items"] = [];

    // Deploy option (if scaled down or not deployed)
    if (record.current_replicas === 0) {
      items.push({
        key: "deploy",
        label: "Deploy",
        icon: <PlayCircleOutlined />,
        onClick: () => handleDeploy(record),
      });
    }

    // Scale down option (if running)
    if (record.current_replicas && record.current_replicas > 0) {
      items.push({
        key: "scale-down",
        label: "Scale Down",
        icon: <PoweroffOutlined />,
        onClick: () => handleScaleDown(record),
      });
    }

    // Delete option (always available)
    items.push({
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDelete(record),
    });

    return { items };
  };

  const formatDuration = (hours: number | null): string => {
    if (hours === null) return "N/A";
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    } else if (hours < 24) {
      return `${hours.toFixed(1)}h`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.round(hours % 24);
      return `${days}d ${remainingHours}h`;
    }
  };

  const formatLastRequest = (lastRequestMs: number | null): string => {
    if (!lastRequestMs) return "No requests";
    return moment(lastRequestMs).fromNow();
  };

  const getDeploymentStatus = (
    currentReplicas: number | null,
    deploymentExists: boolean
  ): { text: string; color: string; icon: React.ReactNode } => {
    if (!deploymentExists) {
      return {
        text: "Not Deployed",
        color: "default",
        icon: <CloseCircleOutlined />,
      };
    }

    if (currentReplicas === null || currentReplicas === 0) {
      return {
        text: "Scaled Down",
        color: "default",
        icon: <CloseCircleOutlined />,
      };
    }

    return {
      text: "Running",
      color: "success",
      icon: <CheckCircleOutlined />,
    };
  };

  const columns: ColumnsType<AgentUptimeMetricsType> = [
    {
      title: "Agent ID",
      dataIndex: "agent_id",
      key: "agent_id",
      width: 100,
      sorter: (a, b) => a.agent_id - b.agent_id,
    },
    {
      title: "Agent Name",
      dataIndex: "agent_name",
      key: "agent_name",
      width: 200,
      sorter: (a, b) => a.agent_name.localeCompare(b.agent_name),
    },
    {
      title: "Company ID",
      dataIndex: "company_id",
      key: "company_id",
      width: 120,
      sorter: (a, b) => (a.company_id || 0) - (b.company_id || 0),
      render: (companyId: number | null) => companyId || "N/A",
    },
    {
      title: "Status",
      key: "status",
      width: 140,
      render: (_, record) => {
        const status = getDeploymentStatus(
          record.current_replicas,
          record.deployment_exists
        );
        return (
          <Tag color={status.color} icon={status.icon}>
            {status.text}
          </Tag>
        );
      },
      sorter: (a, b) => (a.current_replicas || 0) - (b.current_replicas || 0),
    },
    {
      title: "Replicas",
      key: "replicas",
      width: 100,
      render: (_, record) => {
        if (record.current_replicas === null) return "N/A";
        const color = record.current_replicas > 0 ? "green" : "default";
        return (
          <Tag color={color}>
            {record.current_replicas} / {record.desired_replicas}
          </Tag>
        );
      },
      sorter: (a, b) => (a.current_replicas || 0) - (b.current_replicas || 0),
    },
    {
      title: "Deployment Age",
      key: "deployment_age",
      width: 140,
      render: (_, record) => (
        <Tooltip title={`${record.deployment_age_seconds || 0} seconds`}>
          {formatDuration(record.deployment_age_hours)}
        </Tooltip>
      ),
      sorter: (a, b) =>
        (a.deployment_age_hours || 0) - (b.deployment_age_hours || 0),
    },
    {
      title: "Last Request",
      key: "last_request",
      width: 150,
      render: (_, record) => (
        <Tooltip
          title={
            record.last_request_ms
              ? moment(record.last_request_ms).format("YYYY-MM-DD HH:mm:ss")
              : "No requests recorded"
          }>
          {formatLastRequest(record.last_request_ms)}
        </Tooltip>
      ),
      sorter: (a, b) => (a.last_request_ms || 0) - (b.last_request_ms || 0),
    },
    {
      title: "Time Since Last Request",
      key: "time_since_last_request",
      width: 180,
      render: (_, record) => (
        <Tooltip
          title={`${record.time_since_last_request_seconds || 0} seconds`}>
          {formatDuration(record.time_since_last_request_hours)}
        </Tooltip>
      ),
      sorter: (a, b) =>
        (a.time_since_last_request_hours || 0) -
        (b.time_since_last_request_hours || 0),
    },
    {
      title: "Time Till Scale Down",
      key: "time_till_scale_down",
      width: 160,
      render: (_, record) => {
        if (record.time_till_scale_down_hours === null) {
          return <Tag color="default">N/A</Tag>;
        }
        if (record.time_till_scale_down_hours === 0) {
          return <Tag color="error">Eligible for scale down</Tag>;
        }
        const color =
          record.time_till_scale_down_hours < 2
            ? "error"
            : record.time_till_scale_down_hours < 4
            ? "warning"
            : "success";
        return (
          <Tag color={color}>
            {formatDuration(record.time_till_scale_down_hours)}
          </Tag>
        );
      },
      sorter: (a, b) =>
        (a.time_till_scale_down_hours || 0) -
        (b.time_till_scale_down_hours || 0),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={["click"]}>
          <MoreOutlined
            style={{ fontSize: "20px", cursor: "pointer" }}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const regionTabs = Object.keys(metricsByRegion).sort();

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>Agent Uptime Metrics</Title>
      <p style={{ marginBottom: "24px", color: "#666" }}>
        Real-time monitoring of agent deployments across all regions. Data
        refreshes every 30 seconds.
      </p>

      {isLoading && regionTabs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs defaultActiveKey={regionTabs[0]} type="card">
          {regionTabs.map((region) => {
            const agents = metricsByRegion[region] || [];
            const hasError = agents.length > 0 && "error" in agents[0];

            return (
              <TabPane tab={region} key={region}>
                {hasError ? (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#ff4d4f",
                    }}>
                    Error loading data for {region}
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: "16px" }}>
                      <Tag color="blue">Total Agents: {agents.length}</Tag>
                      <Tag color="green">
                        Active Replicas:{" "}
                        {agents.reduce(
                          (sum, agent) => sum + (agent.current_replicas || 0),
                          0
                        )}
                      </Tag>
                      <Tag color="orange">
                        Scaled Down:{" "}
                        {agents.filter((a) => a.current_replicas === 0).length}
                      </Tag>
                    </div>
                    <Table
                      columns={columns}
                      dataSource={agents}
                      rowKey="agent_id"
                      pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} agents`,
                      }}
                      scroll={{ x: 1400 }}
                      size="small"
                    />
                  </>
                )}
              </TabPane>
            );
          })}
        </Tabs>
      )}
    </div>
  );
};

export default AgentUptimeMetrics;
